import React, { useState } from "react";
import styled from "styled-components";

import { useSelector } from "services/redux/rootReducer";

import { Article } from "../../types/article";

import { verifyForm } from "../../functions/form";

import { initialTagSuggestions } from "../../constants/form";

import useSubmit from "features/shared/components/form/hooks/useSubmit";
import BasicInput from "features/shared/components/form/components/basicInput";
import SubmitButton from "features/shared/components/form/components/submitButton";
import TextEditorInput from "features/shared/components/form/components/textEditorInput";
import LabelWrapper from "features/shared/components/form/components/labelWrapper";
import TagSelect from "features/product/components/form/tagSelect/tagSelect";
import useFilteredInitialSuggestions from "features/product/components/form/tagSelect/hooks/useFilteredInitialSuggestions";
import usePureState from "features/shared/hooks/usePureState";
import ImageInput from "features/shared/components/form/components/imageInput";
import CustomCheckbox from "features/shared/components/table/components/customCheckbox";

interface Props {
    onSubmitCallback: (fields: Article) => void;
    submitBtnText: string;
    originalFields?: Article;
    readOnlyMode?: boolean;
    initialForm: Article;
}

const ArticleForm: React.FC<Props> = ({
    onSubmitCallback,
    submitBtnText,
    originalFields,
    readOnlyMode = false,
    initialForm,
}) => {
    const tags = useSelector((state) => state.tags);

    const [fields, setFields] = usePureState<Article>(
        originalFields || initialForm
    );

    const [imageFile, setImageFile] = useState<File | null>();
    const [handleSubmit] = useSubmit({
        fields,
        readOnlyMode,
        handleFormVerification: async (fields) => {
            return await verifyForm({
                fields,
                tags,
            });
        },
        imageUploadOptions: {
            originalImageUrl: originalFields?.properties.image,
            fieldsImageUrl: fields.properties.image,
            imageFile,
            submitAccessor: "properties:image",
        },
        onSubmitCallback,
    });

    const [filteredInitialSuggestions] = useFilteredInitialSuggestions({
        unfilteredInitialSuggestions: initialTagSuggestions,
    });

    const handleRawChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        handleChange(e.target.name, e.target.value);
    };

    const handleChange = (name: string, value: string) => {
        if (readOnlyMode) return;

        let clonedFields = {
            ...fields,
        };

        switch (name) {
            case "properties:name:en":
                clonedFields.properties.name = {
                    en: value,
                    th: clonedFields.properties.name?.th || "",
                };
                break;
            case "properties:name:th":
                clonedFields.properties.name = {
                    en: clonedFields.properties.name?.en || "",
                    th: value,
                };
                break;
            case "properties:description:en":
                clonedFields.properties.description = {
                    en: value,
                    th: clonedFields.properties.description?.th || "",
                };
                break;
            case "properties:description:th":
                clonedFields.properties.description = {
                    en: clonedFields.properties.description?.en || "",
                    th: value,
                };
                break;
            case "properties:rank":
                const rank = parseInt(value);
                if (!rank) delete clonedFields.properties.rank;
                else clonedFields.properties.rank = rank;
                break;
            case "properties:seo":
                clonedFields.properties.seo = value;
                break;
        }

        setFields(clonedFields);
    };

    // Handles the image data
    const handleImageData = (file: File | null) => {
        setImageFile(file);
    };

    // Handles the tags field
    const handleTagData = (tags: Article["tags"]) => {
        setFields({
            ...fields,
            tags,
        });
    };

    // Handles checkbox
    const handleVisiblityToggle = () => {
        setFields({
            ...fields,
            properties: {
                ...fields.properties,
                isHidden: !fields.properties.isHidden,
            },
        });
    };

    return (
        <StyledForm onSubmit={handleSubmit} autoComplete="off">
            <LabelWrapper label="Image">
                <ImageInput
                    initialImageUrl={fields.properties.image}
                    onChangeHandler={handleImageData}
                    readOnlyMode={readOnlyMode}
                />
            </LabelWrapper>
            <LabelWrapper label="Name (EN)">
                <BasicInput
                    name="properties:name:en"
                    value={fields.properties.name?.en}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Name (TH)">
                <BasicInput
                    name="properties:name:th"
                    value={fields.properties.name?.th}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Relevant Tags">
                <TagSelect
                    onChangeHandler={handleTagData}
                    value={fields.tags}
                    initialSuggestions={filteredInitialSuggestions}
                    readOnlyMode={readOnlyMode}
                />
            </LabelWrapper>
            <LabelWrapper label="Content (EN)">
                <TextEditorInput
                    name="properties:description:en"
                    value={fields.properties.description?.en}
                    readOnlyMode={readOnlyMode}
                    onEditorChangeHandler={handleChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Content (TH)">
                <TextEditorInput
                    name="properties:description:th"
                    value={fields.properties.description?.th}
                    readOnlyMode={readOnlyMode}
                    onEditorChangeHandler={handleChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Rank">
                <BasicInput
                    name="properties:rank"
                    value={fields.properties.rank?.toString()}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="SEO">
                <BasicInput
                    name="properties:seo"
                    value={fields.properties.seo}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Hide Article">
                <StyledCustomCheckbox
                    isChecked={fields.properties.isHidden}
                    onClickHandler={handleVisiblityToggle}
                />
            </LabelWrapper>
            {fields.id && (
                <LabelWrapper label="Article ID (Read-only)">
                    <BasicInput
                        name="id"
                        value={fields.id}
                        onChangeHandler={() => null /* readonly */}
                    />
                </LabelWrapper>
            )}
            {!readOnlyMode && <SubmitButton text={submitBtnText} />}
        </StyledForm>
    );
};

const StyledForm = styled.form`
    overflow: auto;
    padding: 5%;
    color: ${(props) => props.theme.colors.neutral[800]};
`;

const StyledCustomCheckbox = styled(CustomCheckbox)`
    justify-content: flex-start;
`;

export default ArticleForm;
